import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@material-ui/lab";
import RefreshIcon from "@material-ui/icons/Refresh";
import {
  getScribeDictationList,
  getAverageTime,
  getTotal,
  getSLA,
  getProviderListScribe,
  getPracticeListScribe,
  getRecentEMRAppointmentLoadDetails,
} from "apiHandler/actions";
import DoctorIconList from "components/DoctorIconList";
import { displayDateFormatter } from "components/helpers";
import useUpdateEffect from "components/customHooks/useUpdateEffect";
import { useUserData } from "components/customHooks/userDataContext";
import UploadDictationModal from "components/modals/UploadDictation";
import ListTable from "components/scribe/listTable";
import SearchDictation from "components/scribe/SearchDictation";
import ListDisctations from "pages/scribe/ListDisctations";
import useSnackbar from "components/customHooks/useSnackbar";
import { useDispatch, useSelector } from "react-redux";
import { setLoadDetails } from "../../redux/reducers/appointment";
import AlertBox from "components/modals/EMRAppointmentLoadDetailsDialog/alertBox";

const allStatusArray = [
  { label: "Total Cases Available", value: null, unit: "", color: "#9CDCE7" },
  { label: "Total Dictation worked", value: null, unit: "", color: "#E2CEF2" },
  { label: "In Scribing", value: null, unit: "", color: "#FEECAF" },
  { label: "On Hold", value: null, unit: "", color: "#F8D9BF" },
  {
    label: "Average Time to Complete (ATC) In Min ",
    value: null,
    color: "#EED6D6",
  },
  { label: "SLA Met %", value: null, unit: "", color: "#CBECC3" },
];

const ScribeAppointments = () => {
  const [type, setType] = useState("doctors");
  const [doctors, setDoctors] = useState(null);
  const [practices, setPractices] = useState(null);
  const [records, setRecords] = useState(null);
  const [allStatus, setAllStatus] = useState([...allStatusArray]);
  const [uploadDictn, setUploadDict] = useState(false);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const dispatch = useDispatch();
  const active = useRef(true);
  const [loading, setLoading] = useState(true); // to show loader while fetching count
  // It is for both doctors and Practices: Later make an array //
  const [selectedDoctor, setSelectedDoctor] = useState("all");
  const { CustomSnackbar, setSnackbarMessage } = useSnackbar();
  const loadDetails = useSelector((state) => state.appointment.loadDetails);
  const navigate = useNavigate();

  const userData = useUserData();
  const id_token = userData?.access_token;

  useEffect(() => {
    const source = axios.CancelToken.source();
    refreshPage(source.token);
    const refresh = setInterval(() => {
      if (active.current) refreshPage(source.token, false);
    }, 30000); // 30 seconds = 30 * 1000
    return () => {
      clearTimeout(refresh);
      // source.cancel();
    };
    // eslint-disable-next-line
  }, []);

  // To not call refreshPage when user is not active on dashboard tab
  const onFocus = () => {
    active.current = true;
  };

  const onBlur = () => {
    active.current = false;
  };

  useEffect(() => {
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    // Specify how to clean up after this effect:
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  const fetchStats = async (token) => {
    const payload = (metric_name, needNpi = false) => {
      let request = {
        type: "Scribe",
        search_criteria: {
          metric_name: metric_name,
          scribe_id: userData.email,
        },
      };
      if (needNpi) {
        if (type === "doctors" && selectedDoctor !== "all") {
          request.search_criteria["npi"] = [selectedDoctor];
        } else if (type === "practice" && selectedDoctor !== "all") {
          request.search_criteria["practice_id"] = [selectedDoctor];
        }
      }
      return request;
    };

    const totalCases = getTotal(
      payload("TotalCasesAvailable", true),
      id_token,
      token,
    );
    const totalWorked = getTotal(
      payload("TotalCasesWorked", true),
      id_token,
      token,
    );
    const inProgress = getTotal(
      payload("TotalCasesInScribing", true),
      id_token,
      token,
    );
    const onHold = getTotal(payload("TotalCasesOnHold", true), id_token, token);
    const averageTime = getAverageTime(
      payload("AvgTimeCompletion"),
      id_token,
      token,
    );
    const SLA = getSLA(payload("SLAMet"), id_token, token);

    const result = await Promise.allSettled([
      totalCases,
      totalWorked,
      inProgress,
      onHold,
      averageTime,
      SLA,
    ]);

    const updateTotalCount = [...allStatus];
    for (let i = 0; i < result.length; i++) {
      updateTotalCount[i].value =
        result[i].status === "fulfilled" ? result[i].value : "N/A";
    }
    setAllStatus(updateTotalCount);
  };

  useUpdateEffect(() => {
    getStats();
  }, [selectedDoctor]);

  const getEMRAppointmentsLoadDetails = async () => {
    try {
      const payload = {
        practice_id: `${userData.selected_practice}`,
      };

      const res = await getRecentEMRAppointmentLoadDetails(payload, id_token);

      if (Array.isArray(res)) {
        dispatch(setLoadDetails(res));
      } else {
        dispatch(setLoadDetails([]));
        setSnackbarMessage("No load details found");
      }
    } catch (error) {
      console.log("error: ", error);
      dispatch(setLoadDetails([]));
      setSnackbarMessage(
        error?.response?.data?.message || "Failed to fetch load details",
      );
    }
  };

  const getStats = async () => {
    setLoading(true);
    await fetchStats();
    setLoading(false);
  };

  const refreshPage = async (token, showLoader = true) => {
    if (showLoader) setLoading(true);
    await Promise.allSettled([
      fetchProviders(token),
      fetchDictation(token),
      fetchStats(token),
      getEMRAppointmentsLoadDetails(),
    ]);
    setLoading(false);
  };

  const onRefresh = async () => {
    // setting initial States
    setDoctors(null);
    setPractices(null);
    setRecords(null);
    setLoading(true);
    setCurrentPlaying(null);
    // refreshing the page
    await refreshPage();
    setLoading(false);
  };

  const fetchProviders = async (token) => {
    await setType("doctors");
    const payload = {
      type:
        userData.user_details.user_role === "Provider" ? "Dictation" : "Scribe",
      practice_id: userData?.selected_practice,
      search_criteria: {
        scribe_id: userData.email,
      },
    };
    const response = await getProviderListScribe(payload, id_token, token);
    setDoctors(
      response.map((r) => {
        return {
          ...r,
          id: r.npi,
          name: r.first_name + " " + r.last_name,
          shortName: r.first_name?.[0] + r.last_name?.[0],
        };
      }),
    );
  };

  const fetchDictation = async (token) => {
    const payload = {
      type: "Scribe",
      search_criteria: {
        scribe_id: userData.email,
        status: "In-Scribing",
        limit: 5,
      },
    };
    let response = await getScribeDictationList(payload, id_token, token);
    setRecords(
      response.map((r) => {
        return {
          ...r,
          updated_date: displayDateFormatter(r.updated_date),
          created_date: displayDateFormatter(r.created_date),
          dos: displayDateFormatter(r.dos),
        };
      }),
    );
  };

  const getPractices = async () => {
    await setType("practice");
    if (practices === null) {
      const payload = {
        type: "Scribe",
        search_criteria: {
          scribe_id: userData.email,
        },
      };
      const response = await getPracticeListScribe(payload, id_token);
      setPractices(
        response.map((r) => {
          return {
            ...r,
            id: r.practice_id,
            name: r.practice_name,
            shortName: r.practice_name?.[0],
          };
        }),
      );
    }
  };

  const name = userData.first_name
    ? userData.first_name + " " + userData.last_name
    : "Scribe";
  return (
    <>
      <CustomSnackbar />
      {userData.user_details.user_role === "Provider" ? (
        <ListDisctations />
      ) : (
        <div className="scribe-dashboard">
          <div className="header">
            <h1>Hello {name}</h1>
            <div className="btn-div">
              <SearchDictation
                type={type}
                doctors={doctors}
                practices={practices}
              />
              <button
                className="blank-hover-btn"
                onClick={() => setUploadDict(true)}
              >
                Upload Dictation
              </button>
              <button className="blank-hover-btn">
                <RefreshIcon
                  onClick={onRefresh}
                  fontSize="medium"
                  color="secondary"
                />
              </button>
            </div>
          </div>
          <AlertBox rows={loadDetails} />
          {uploadDictn && (
            <UploadDictationModal
              onClose={() => setUploadDict(false)}
              setSnackbarMessage={setSnackbarMessage}
            />
          )}
          <div className="main">
            <div className="select-doctors">
              <div className="btn-cont">
                <button
                  className={`button ${type === "doctors" ? "sel-btn" : ""}`}
                  onClick={() => setType("doctors")}
                >
                  Doctors
                </button>
                <button
                  className={`button ${type === "practice" ? "sel-btn" : ""}`}
                  onClick={getPractices}
                >
                  Practice
                </button>
              </div>
              <DoctorIconList
                type={type === "doctors" ? doctors : practices}
                selected={selectedDoctor}
                setSelected={setSelectedDoctor}
              />
            </div>
            <div className="dr-status-cont">
              <p className="title">last 30 days</p>
              <div className="doctor-status">
                {allStatus.map((s, index) => {
                  return (
                    <div
                      style={{ backgroundColor: s.color, cursor: "pointer" }}
                      className="status-container"
                      key={index}
                      onClick={() => {
                        if (
                          s.label === "In Scribing" ||
                          s.label === "Total Cases Available" ||
                          s.label === "Total Dictation worked" ||
                          s.label === "On Hold"
                        ) {
                          navigate("/scribe-status-list", {
                            state: { ...s, selectedDoctor, type, status: s },
                          });
                        }
                      }}
                    >
                      <p className="label">{s.label}</p>
                      {loading ? (
                        <Skeleton animation="wave" />
                      ) : (
                        <p className="value">
                          {s.value} {s.value ? s.unit : ""}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="list-table">
            <p className="title">Needs Attention</p>
            <ListTable
              records={records}
              currentPlaying={currentPlaying}
              setCurrentPlaying={setCurrentPlaying}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ScribeAppointments;
